/**
 * Jobs Page
 * 
 * List and manage GPU jobs.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  Cpu,
  RefreshCw,
  Trash2,
  ExternalLink,
  Filter,
  Box,
  Layers,
  Clock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { gpuJobsApi, GpuJob } from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useState } from 'react';
import { useProgress } from '../contexts/ProgressContext';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function JobsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  
  // Use shared progress tracking
  const { startProgress, completeProgress } = useProgress();

  const { data: jobs = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['gpu-jobs'],
    queryFn: gpuJobsApi.list,
    refetchInterval: 30000,
  });

  const cancelMutation = useMutation({
    mutationFn: async (jobId: string) => {
      // Get the job's network details for AWS-validated progress tracking
      const job = jobs.find(j => j.id === jobId);
      
      // Start progress with initial resources for AWS validation
      // The polling will continue even if the API call times out
      startProgress(jobId, {
        vpcId: job?.vpcId,
        subnetId: job?.subnetId,
        securityGroupId: job?.securityGroupId,
        instanceId: job?.instanceId,
      });
      
      try {
        return await gpuJobsApi.cancel(jobId);
      } catch (error: any) {
        // If timeout, don't fail - let the AWS polling continue
        if (error.message?.includes('timeout')) {
          toast(
            <div>
              <p className="font-medium">Cancellation in progress</p>
              <p className="text-sm opacity-80 mt-1">Backend timed out, but we're still tracking progress via AWS polling</p>
            </div>,
            { duration: 4000, icon: '⏳' }
          );
          // Return a minimal response to avoid errors
          return { 
            id: jobId, 
            status: 'cancelled' as const,
            networkCleanup: { attempted: true, success: false, message: 'Polling AWS for status...' } 
          } as any;
        }
        throw error;
      }
    },
    onSuccess: (data, jobId) => {
      // Show toast with link to operations page if executionId is returned
      if (data.executionId) {
        toast(
          <div>
            <p className="font-medium">Job cancellation started</p>
            <button
              onClick={() => navigate('/operations')}
              className="text-blue-400 hover:text-blue-300 text-sm mt-1 underline"
            >
              View progress →
            </button>
          </div>,
          { duration: 5000 }
        );
      }

      // If this was a timeout response, progress polling will continue
      if (data.networkCleanup?.message?.includes('Polling AWS')) {
        queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
        return;
      }
      
      // Check for network cleanup info
      const networkCleanup = data.networkCleanup;
      
      if (networkCleanup?.attempted) {
        if (networkCleanup.sharedWithOtherInstances) {
          completeProgress(
            jobId, 
            true, 
            'Network preserved (shared with other instances)',
            true
          );
          toast.success(
            <div>
              <p className="font-medium">Job cancelled successfully</p>
              <p className="text-sm opacity-80 mt-1">Network preserved (shared with other instances)</p>
            </div>,
            { duration: 5000 }
          );
        } else if (networkCleanup.success) {
          // DON'T complete progress here - let AWS polling verify network cleanup
          toast(
            <div>
              <p className="font-medium">Job cancelled - Network cleanup initiated</p>
              <p className="text-sm opacity-80 mt-1">Removing VPC, security groups, subnets...</p>
              <p className="text-xs opacity-60 mt-1">Progress tracked via AWS polling</p>
            </div>,
            { duration: 4000, icon: '🔄' }
          );
        } else {
          // Cleanup was attempted but failed
          completeProgress(
            jobId, 
            false, 
            networkCleanup.message || 'Network cleanup failed',
            true
          );
          toast(
            <div>
              <p className="font-medium text-yellow-300">Job cancelled but network cleanup failed</p>
              <p className="text-sm opacity-80 mt-1">{networkCleanup.message}</p>
              <p className="text-xs opacity-70 mt-2">You may need to manually remove the network from the Network page.</p>
            </div>,
            { duration: 8000, icon: '⚠️' }
          );
        }
      } else {
        // No network cleanup info - might be an old job or no instance/network
        completeProgress(jobId, true, 'Job cancelled successfully');
        toast.success('Job cancelled successfully');
      }
      
      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
      queryClient.invalidateQueries({ queryKey: ['network-status'] });
    },
    onError: (error: Error, jobId) => {
      // Don't complete progress on timeout - let AWS polling continue
      // Only complete for non-timeout errors
      if (!error.message?.includes('timeout')) {
        completeProgress(jobId, false, `Failed: ${error.message}`);
      }
      
      toast.error(
        <div>
          <p className="font-medium">{error.message?.includes('timeout') ? 'API timeout' : 'Failed to cancel job'}</p>
          <p className="text-sm opacity-80 mt-1">{error.message}</p>
          {error.message?.includes('timeout') && (
            <p className="text-xs opacity-70 mt-2">Still tracking progress via AWS polling...</p>
          )}
        </div>,
        { duration: 6000 }
      );
      queryClient.invalidateQueries({ queryKey: ['gpu-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['detailed-network-status'] });
      queryClient.invalidateQueries({ queryKey: ['network-status'] });
    },
  });

  // Filter and paginate jobs
  let filteredJobs = statusFilter === 'all'
    ? jobs
    : jobs.filter((j) => j.status === statusFilter);

  // Filter by date range
  if (dateFrom || dateTo) {
    filteredJobs = filteredJobs.filter(j => {
      if (!j.createdAt) return false;
      const jobDate = new Date(j.createdAt);
      const fromDate = dateFrom ? new Date(dateFrom) : null;
      const toDate = dateTo ? new Date(dateTo) : null;
      
      // Set time to start/end of day for proper comparison
      if (fromDate) fromDate.setHours(0, 0, 0, 0);
      if (toDate) toDate.setHours(23, 59, 59, 999);
      
      if (fromDate && jobDate < fromDate) return false;
      if (toDate && jobDate > toDate) return false;
      return true;
    });
  }

  // Calculate pagination
  const totalPages = Math.ceil(filteredJobs.length / perPage);
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedJobs = filteredJobs.slice(startIndex, endIndex);

  const getStatusBadge = (status: GpuJob['status']) => {
    const styles = {
      pending: 'badge-warning',
      provisioning: 'badge-info',
      running: 'badge-success',
      stopping: 'badge-warning',
      stopped: 'badge-warning',
      completed: 'badge-neutral',
      failed: 'badge-error',
      cancelled: 'badge-neutral',
      terminated: 'badge-neutral',
    };
    return styles[status] || 'badge-neutral';
  };

  const canCancel = (status: GpuJob['status']) =>
    ['pending', 'provisioning', 'running'].includes(status);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-surface-100 mb-1 sm:mb-2">GPU Jobs</h1>
          <p className="text-sm sm:text-base text-surface-400">
            Manage your GPU computing jobs
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-surface-500 hidden sm:flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Updated less than a minute ago
          </span>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="btn-ghost"
          >
            <RefreshCw className={clsx('w-4 h-4', isFetching && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants} className="space-y-4">
        {/* Status Filter */}
        <div className="flex items-center gap-3">
          <Filter className="w-4 h-4 text-surface-400" />
          <div className="flex items-center gap-1 p-1 bg-surface-900/50 rounded-xl border border-surface-800/50 overflow-x-auto">
            {['all', 'running', 'pending', 'completed', 'failed'].map((status) => (
              <button
                key={status}
                onClick={() => {
                  setStatusFilter(status);
                  setPage(1);
                }}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                  statusFilter === status
                    ? 'bg-surface-800 text-white shadow-sm'
                    : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800/50',
                )}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Date Range and Page Size Filters */}
        {jobs.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 flex-wrap p-3 sm:p-4 rounded-xl bg-surface-900/50 border border-surface-800/50">
            {/* Date Range Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <Clock className="w-4 h-4 text-surface-400" />
              <span className="text-sm text-surface-400">Date Range:</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-1.5 rounded-lg text-sm bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:border-gpu-500 transition-colors"
                placeholder="From"
              />
              <span className="text-surface-500">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-1.5 rounded-lg text-sm bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:border-gpu-500 transition-colors"
                placeholder="To"
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                    setPage(1);
                  }}
                  className="px-2 py-1 rounded-lg text-xs bg-surface-700 text-surface-300 hover:bg-surface-600 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Page Size Selector */}
            <div className="flex items-center gap-2 sm:ml-auto">
              <span className="text-sm text-surface-400">Per page:</span>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-1.5 rounded-lg text-sm bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:border-gpu-500 transition-colors cursor-pointer"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        )}
      </motion.div>

      {/* Jobs List */}
      <motion.div variants={itemVariants} className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-gpu-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-16">
            <Cpu className="w-16 h-16 text-surface-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-surface-300 mb-2">No jobs found</h3>
            <p className="text-surface-500">
              {statusFilter === 'all'
                ? 'Get started by launching your first GPU job'
                : `No ${statusFilter} jobs at the moment`}
            </p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-surface-800">
              {paginatedJobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-4 sm:p-6 hover:bg-surface-800/30 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                      <Link
                        to={`/jobs/${job.id}`}
                        className="font-mono text-sm sm:text-base text-gpu-400 hover:text-gpu-300 font-medium truncate"
                      >
                        {job.id}
                      </Link>
                      <span className={getStatusBadge(job.status)}>
                        {job.status}
                      </span>
                      {job.computeType === 'ecs' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                          <Layers className="w-3 h-3" />
                          ECS
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-orange-300 border border-orange-500/30">
                          <Box className="w-3 h-3" />
                          EC2
                        </span>
                      )}
                      {job.useSpot && (
                        <span className="badge-info">Spot</span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 text-sm">
                      <div>
                        <span className="text-surface-500">Instance:</span>
                        <span className="ml-2 font-mono text-surface-300">
                          {job.instanceType}
                        </span>
                      </div>
                      <div>
                        <span className="text-surface-500">GPUs:</span>
                        <span className="ml-2 text-surface-300">{job.gpuCount}</span>
                      </div>
                      <div>
                        <span className="text-surface-500">Runtime:</span>
                        <span className="ml-2 text-surface-300">{job.maxRuntimeHours}h</span>
                      </div>
                      <div>
                        <span className="text-surface-500">Cost:</span>
                        <span className="ml-2 text-surface-300">
                          ${job.estimatedCostUsd.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center gap-4 text-xs text-surface-500">
                      <span>
                        Created {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
                      </span>
                      {job.instanceId && (
                        <span className="font-mono">{job.instanceId}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Link
                      to={`/jobs/${job.id}`}
                      className="btn-ghost p-2"
                      title="View Details"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                    {canCancel(job.status) && (
                      <button
                        onClick={() => {
                          if (confirm('Are you sure you want to cancel this job?')) {
                            cancelMutation.mutate(job.id);
                          }
                        }}
                        disabled={cancelMutation.isPending}
                        className="btn-ghost p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        title="Cancel Job"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 sm:p-6 border-t border-surface-800">
                <div className="text-sm text-surface-400">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredJobs.length)} of {filteredJobs.length} jobs
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                      // Show first, last, current, and pages around current
                      if (
                        p === 1 ||
                        p === totalPages ||
                        (p >= page - 1 && p <= page + 1)
                      ) {
                        return (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            className={clsx(
                              'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                              p === page
                                ? 'bg-gpu-500 text-white'
                                : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                            )}
                          >
                            {p}
                          </button>
                        );
                      } else if (
                        p === page - 2 ||
                        p === page + 2
                      ) {
                        return <span key={p} className="text-surface-500">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
